import { useEffect, useRef, useState } from "react";
import {
  Animated,
  Easing,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import {
  NavigationProp,
  ParamListBase,
  RouteProp,
  useNavigation,
} from "@react-navigation/native";
import { FontAwesome } from "@expo/vector-icons";
import { RootStackParamList } from "../../navigators/StackNavigator";

// Data
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux";
import { InsightID, Insight, Entity as EntityType } from "../../types/data";

// Components
import Entity from "../common/BottomSheet/Entity";
import Button from "../common/Button";
import Card from "../common/Card";

// Styles
import { Colors, Spacing } from "../../styles";
import { selectStories, setViewingStory } from "../../redux/mainSlice";

const Stories = ({ currentStoryIndex }: { currentStoryIndex: number }) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();

  //#region Data
  const stories = useSelector(selectStories);
  const story = stories.data[currentStoryIndex];

  if (!story) return <></>;

  const insightIDs: InsightID[] = story.insights;
  const insights = useSelector((state: RootState) =>
    insightIDs.map((id) =>
      state.main.insights.find((insight) => insight.id === id)
    )
  ) as Insight[];

  const entity = story.entity;
  //#endregion

  //#region Animation
  const [storyIndex, setStoryIndex] = useState(0);
  const widthAnim = useRef(new Animated.Value(0)).current;
  const anim = (index?: number) =>
    Animated.timing(widthAnim, {
      toValue: 1,
      duration: 4000,
      useNativeDriver: false,
      easing: Easing.linear,
    }).start((o) => {
      if (o.finished) {
        const newIndex = index ? index + 1 : 1;
        if (newIndex < insights.length) {
          setStoryIndex(newIndex);
          widthAnim.setValue(0);
          anim(newIndex);
        } else {
          dispatch(
            setViewingStory(stories.data[currentStoryIndex + 1]?.entity)
          );
        }
      }
    });

  useEffect(() => {
    widthAnim.setValue(0);
    anim(storyIndex);
  }, [story]);
  // Start on screen focus
  // useEffect(() => {
  //   const unsubscribe = navigation.addListener("focus", () => {
  //     widthAnim.setValue(0);
  //     anim(storyIndex);
  //   });

  //   return unsubscribe;
  // }, [navigation, storyIndex]);

  // Stop on screen blur
  // useEffect(() => {
  //   const unsubscribe = navigation.addListener("blur", () => {
  //     Animated.timing(widthAnim, { toValue: 0, useNativeDriver: false }).stop();
  //   });

  //   return unsubscribe;
  // }, [navigation]);
  //#endregion

  return (
    <View style={styles.container}>
      {/* Header */}
      <View>
        {/* Progress bars */}
        <View style={styles.progressBars}>
          {insights.map((_, index) => (
            <View
              style={[
                styles.progressBar,
                {
                  width: 100 / insights.length - 2 + "%",
                  backgroundColor:
                    index < storyIndex ? Colors.white : Colors.plum500,
                },
              ]}
            >
              {index === storyIndex && (
                <Animated.View
                  style={[
                    styles.progressBarInner,
                    {
                      width: widthAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: ["0%", "100%"],
                      }),
                    },
                  ]}
                />
              )}
            </View>
          ))}
        </View>
        {/* Entity */}
        <Entity entity={entity} size={"large"} isOpaque={true} />
      </View>

      <View style={styles.content}>
        {/* Card */}
        <Card id={insights[storyIndex]?.id} />

        {/* Back/Forward navigation container */}
        <View style={styles.navContainer} pointerEvents="box-none">
          <TouchableWithoutFeedback
            onPress={() => {
              let newIndex = storyIndex - 1;

              if (newIndex < 0) {
                dispatch(
                  setViewingStory(stories.data[currentStoryIndex - 1]?.entity)
                );
                return;
              }

              widthAnim.setValue(0);
              anim(newIndex);
              setStoryIndex(newIndex);
            }}
          >
            <View style={{ width: "50%", height: "100%" }} />
          </TouchableWithoutFeedback>
          <TouchableWithoutFeedback
            onPress={() => {
              let newIndex = storyIndex + 1;

              if (newIndex === insights.length) {
                dispatch(
                  setViewingStory(stories.data[currentStoryIndex + 1]?.entity)
                );
                return;
              }

              newIndex = Math.min(insights.length - 1, newIndex);
              widthAnim.setValue(0);
              anim(newIndex);
              setStoryIndex(newIndex);
            }}
          >
            <View style={{ width: "50%", height: "100%" }} />
          </TouchableWithoutFeedback>
        </View>
      </View>

      {/* View insight */}
      <Button
        title="View insight"
        onPress={() => {
          dispatch(setViewingStory(undefined));
          navigation.navigate("Insight", {
            id: insightIDs[storyIndex],
          });
        }}
        icon={
          <FontAwesome name="long-arrow-right" size={14} color={Colors.white} />
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: "100%",
    // flex: 1,
    padding: Spacing.page,
    justifyContent: "space-between",
    position: "relative",
  },
  progressBars: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 15,
  },
  progressBar: {
    height: 2,
    backgroundColor: Colors.plum500,
  },
  progressBarInner: {
    height: "100%",
    backgroundColor: Colors.white,
  },
  content: {
    flex: 1,
    position: "relative",
    display: "flex",
    justifyContent: "center",
  },
  navContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

export default Stories;
